// Generic per-source connect flow (Stage 6, DEC-3/15/24).
//
// Replaces the hardcoded Google OAuth popup + TelegramAuthModal. Given a source's
// `auth_type` it either:
//   - oauth2     → navigates the browser to the host-owned start route
//                  (`/auth/sources/<id>/start`); the host runs the whole ceremony
//                  and redirects back with `?source_connected=<id>`.
//   - phone_code → opens a host auth-session and renders the source's own
//                  `auth/screen.tsx` (loaded + transpiled by the backend), wiring
//                  its `submit` / `exec` props to the WS `source.auth.*` surface.
//
// No secret/token is ever held here — the host owns all of it (CON-3 / DEC-14).

import { useCallback, useEffect, useState } from "react";
import type { ComponentType, JSX } from "react";

import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import {
  loadSourceAuthScreen,
  sourceStartUrl,
} from "../../runtime/plugins/loader";

/// The contract a source's `auth/screen.tsx` default export implements. The host
/// injects the session drivers so the screen never talks to the transport itself.
export interface SourceAuthScreenProps {
  readonly sourceId: string;
  readonly submit: (
    step: "phone" | "code" | "password",
    value: string,
  ) => Promise<void>;
  readonly exec: (op: "begin" | "step") => Promise<{ status: string }>;
  readonly onConnected?: () => void;
}

interface ScreenModule {
  readonly default: ComponentType<SourceAuthScreenProps>;
}

export interface SourceConnectProps {
  readonly sourceId: string;
  readonly authType: string;
  readonly onConnected?: () => void;
  readonly onError?: (message: string) => void;
}

export function SourceConnect({
  sourceId,
  authType,
  onConnected,
  onError,
}: SourceConnectProps): JSX.Element {
  if (authType === "oauth2") {
    return <OAuthConnect sourceId={sourceId} onConnected={onConnected} />;
  }
  if (authType === "phone_code") {
    return (
      <PhoneCodeConnect
        sourceId={sourceId}
        onConnected={onConnected}
        onError={onError}
      />
    );
  }
  if (authType === "api_key") {
    return (
      <ApiKeyConnect
        sourceId={sourceId}
        onConnected={onConnected}
        onError={onError}
      />
    );
  }
  return (
    <p className="text-[13px] text-content-secondary">
      This source does not support interactive connect.
    </p>
  );
}

/// oauth2: open the host start route in a POPUP (a new browser window, or a Tauri
/// in-app webview) rather than navigating the SPA away — the desktop app's main
/// webview must not leave the app, and a popup is a cleaner web UX too. The host
/// runs the whole ceremony in the popup and, on success, emits
/// `source.account.connected` over WS; we close the modal on that (the popup
/// closes itself — see `main.tsx`).
function OAuthConnect({
  sourceId,
  onConnected,
}: {
  readonly sourceId: string;
  readonly onConnected?: () => void;
}): JSX.Element {
  const runtime = useAppRuntime();
  const [opened, setOpened] = useState(false);

  // The host signals completion over WS once the popup finishes the exchange.
  useEffect(() => {
    return runtime.transport.onEventType(["source.account.connected"], () => {
      onConnected?.();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = useCallback(() => {
    const url = new URL(sourceStartUrl(sourceId), window.location.origin).href;
    setOpened(true);
    // Tauri: a dedicated in-app webview (Rust `open_oauth_window`); browser: a popup.
    if ("__TAURI_INTERNALS__" in window || "__TAURI__" in window) {
      void import("@tauri-apps/api/core").then(({ invoke }) =>
        invoke("open_oauth_window", { url }),
      );
    } else {
      // `popup=yes` forces a real popup WINDOW (not a new tab) in Chromium/Brave;
      // centre it over the current window.
      const w = 520;
      const h = 720;
      const left = Math.round(
        window.screenX + Math.max(0, (window.outerWidth - w) / 2),
      );
      const top = Math.round(
        window.screenY + Math.max(0, (window.outerHeight - h) / 2),
      );
      const features = `popup=yes,width=${String(w)},height=${String(h)},left=${String(left)},top=${String(top)}`;
      window.open(url, "magnis-oauth", features);
    }
  }, [sourceId]);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px] text-content-secondary">
        {opened
          ? "Complete sign-in in the window that opened, then return here."
          : "A window will open for you to authorize access."}
      </p>
      <button
        type="button"
        onClick={open}
        className="w-full rounded-lg bg-white px-4 py-2.5 text-sm font-medium text-black cursor-pointer border-none"
      >
        {opened ? "Reopen sign-in window" : "Connect"}
      </button>
    </div>
  );
}

/// phone_code: open the host session, load the source's screen, drive it over WS.
function PhoneCodeConnect({
  sourceId,
  onConnected,
  onError,
}: {
  readonly sourceId: string;
  readonly onConnected?: () => void;
  readonly onError?: (message: string) => void;
}): JSX.Element {
  const runtime = useAppRuntime();
  const [Screen, setScreen] = useState<ComponentType<SourceAuthScreenProps> | null>(
    null,
  );
  const [session_id, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const init = async (): Promise<void> => {
      try {
        const opened = await runtime.transport.rpc<{ session_id: string }>(
          "source.auth.session.open",
          { source_id: sourceId },
        );
        const mod = await loadSourceAuthScreen<ScreenModule>(sourceId);
        if (cancelled) return;
        setSessionId(opened.session_id);
        setScreen(() => mod.default);
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        if (cancelled) return;
        setError(message);
        onError?.(message);
      }
    };
    void init();
    return (): void => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId]);

  if (error) {
    return <p className="text-[13px] text-red-400">{error}</p>;
  }
  if (!Screen || !session_id) {
    return <p className="text-[13px] text-content-secondary">Loading&hellip;</p>;
  }

  const submit = async (
    step: "phone" | "code" | "password",
    value: string,
  ): Promise<void> => {
    await runtime.transport.rpc("source.auth.submit", {
      source_id: sourceId,
      session_id: session_id,
      step,
      value,
    });
  };
  const exec = async (op: "begin" | "step"): Promise<{ status: string }> => {
    return runtime.transport.rpc<{ status: string }>("source.auth.exec", {
      source_id: sourceId,
      session_id: session_id,
      op,
    });
  };

  return (
    <Screen
      sourceId={sourceId}
      submit={submit}
      exec={exec}
      onConnected={onConnected}
    />
  );
}


/// api_key (scn_sources_accounts_001): enter the source's key(s), then
/// EXPLICITLY provision the account — a clean DB has zero accounts, and this
/// click is the one and only thing that creates one. Keys already configured
/// (vault or env) are shown as such and don't need re-entry.
function ApiKeyConnect({
  sourceId,
  onConnected,
  onError,
}: {
  readonly sourceId: string;
  readonly onConnected?: () => void;
  readonly onError?: (message: string) => void;
}): JSX.Element {
  const runtime = useAppRuntime();
  const [keys, setKeys] = useState<
    { key: string; label: string; configured: boolean }[] | null
  >(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false as boolean;
    void (async (): Promise<void> => {
      try {
        const list = await runtime.transport.rpc<{
          sources: {
            source_id: string;
            keys: {
              key: string;
              label: string;
              vaultConfigured: boolean;
            }[];
          }[];
        }>("source.keys.list");
        if (cancelled) return;
        const entry = list.sources.find((s) => s.source_id === sourceId);
        setKeys(
          (entry ? entry.keys : []).map((k) => ({
            key: k.key,
            label: k.label,
            // Configured = in the vault. NO env (credentials-model §5.4).
            configured: k.vaultConfigured,
          })),
        );
      } catch (e) {
        if (!cancelled) onError?.(e instanceof Error ? e.message : String(e));
      }
    })();
    return (): void => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId]);

  const missing = (keys ?? []).filter((k) => !k.configured);
  const canConnect =
    keys !== null && missing.every((k) => (values[k.key] ?? "").trim().length > 0);

  const handleConnect = async (): Promise<void> => {
    setBusy(true);
    try {
      for (const k of missing) {
        await runtime.transport.rpc("source.keys.set", {
          source_id: sourceId,
          key: k.key,
          value: (values[k.key] ?? "").trim(),
        });
      }
      await runtime.transport.rpc("source.accounts.provision", {
        source_id: sourceId,
      });
      onConnected?.();
    } catch (e) {
      onError?.(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (keys === null) {
    return <p className="text-[13px] text-content-secondary">Loading…</p>;
  }
  return (
    // A real form so Enter in a key field submits (the default action).
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (canConnect && !busy) void handleConnect();
      }}
    >
      {keys.map((k) =>
        k.configured ? (
          <p key={k.key} className="text-[13px] text-content-secondary m-0">
            {k.label}: already configured
          </p>
        ) : (
          <label key={k.key} className="flex flex-col gap-1 text-[13px] text-content">
            {k.label}
            <input
              type="password"
              value={values[k.key] ?? ""}
              onChange={(e) => {
                setValues((v) => ({ ...v, [k.key]: e.target.value }));
              }}
              className="rounded-lg border border-edge bg-surface px-3 py-2 text-content"
              placeholder="paste the key"
            />
          </label>
        ),
      )}
      <button
        type="submit"
        disabled={!canConnect || busy}
        className="mt-1 rounded-lg bg-accent px-4 py-2 text-[13px] font-medium text-white disabled:opacity-50 cursor-pointer border-none"
      >
        {busy ? "Connecting…" : "Connect"}
      </button>
    </form>
  );
}
